import { create } from 'zustand';

import { authoritiesAPI } from '../api/authorities';

/**
 * Store for authority check-all and run-all operations
 * Persists across navigation so users can see progress when they return
 */
export const useAuthorityCheckStore = create((set, get) => ({
  // State
  checkingAll: false,
  checkAllProgress: { done: 0, total: 0 },
  checkAllAbortController: null,
  runningAll: false,
  runAllProgress: { done: 0, total: 0 },
  runAllAbortController: null,

  // Actions
  setCheckingAll: (checking) => set({ checkingAll: checking }),
  setCheckAllProgress: (progress) => set({ checkAllProgress: progress }),
  setRunningAll: (running) => set({ runningAll: running }),
  setRunAllProgress: (progress) => set({ runAllProgress: progress }),

  // Check all authorities
  checkAll: async (typeFilter, typeNotFilter, qc, tabKey) => {
    const abortController = new AbortController();
    set({
      checkingAll: true,
      checkAllProgress: { done: 0, total: 0 },
      checkAllAbortController: abortController,
    });

    try {
      // Fetch ALL authorities, not just current page
      const allData = await authoritiesAPI.list({
        type: typeFilter || undefined,
        typeNot: typeNotFilter || undefined,
        limit: 10000,
      });
      const toCheck = (allData.authorities ?? []).filter((a) => a.calendarUrl);

      if (toCheck.length === 0) {
        set({ checkingAll: false, checkAllAbortController: null });
        return;
      }

      set({ checkAllProgress: { done: 0, total: toCheck.length } });

      for (let i = 0; i < toCheck.length; i++) {
        // Check if abort was requested
        if (abortController.signal.aborted) {
          break;
        }

        try {
          await authoritiesAPI.triggerCheck(toCheck[i].id, abortController.signal);
          set({ checkAllProgress: { done: i + 1, total: toCheck.length } });
        } catch (err) {
          if (err.code === 'ERR_CANCELED') {
            break;
          }
          console.error(`Failed to check ${toCheck[i].name}:`, err);
          set({ checkAllProgress: { done: i + 1, total: toCheck.length } });
        }
        // Stagger requests: 500ms between checks
        if (i < toCheck.length - 1) await new Promise((r) => setTimeout(r, 500));
      }

      qc.invalidateQueries({ queryKey: ['admin-authorities', tabKey] });
    } finally {
      set({
        checkingAll: false,
        checkAllProgress: { done: 0, total: 0 },
        checkAllAbortController: null,
      });
    }
  },

  abortCheckAll: () => {
    const controller = get().checkAllAbortController;
    if (controller) {
      controller.abort();
    }
  },

  // Run all scrapers
  runAll: async (typeFilter, typeNotFilter, qc, tabKey) => {
    const abortController = new AbortController();
    set({
      runningAll: true,
      runAllProgress: { done: 0, total: 0 },
      runAllAbortController: abortController,
    });

    try {
      // Fetch ALL authorities, not just current page
      const allData = await authoritiesAPI.list({
        type: typeFilter || undefined,
        typeNot: typeNotFilter || undefined,
        limit: 10000,
      });
      const toRun = (allData.authorities ?? []).filter((a) => a.scraperKey);

      if (toRun.length === 0) {
        set({ runningAll: false, runAllAbortController: null });
        return;
      }

      set({ runAllProgress: { done: 0, total: toRun.length } });

      for (let i = 0; i < toRun.length; i++) {
        // Check if abort was requested
        if (abortController.signal.aborted) {
          break;
        }

        try {
          await authoritiesAPI.runScraper(toRun[i].id, abortController.signal);
          set({ runAllProgress: { done: i + 1, total: toRun.length } });
        } catch (err) {
          if (err.code === 'ERR_CANCELED') {
            break;
          }
          console.error(`Failed to run scraper for ${toRun[i].name}:`, err);
          set({ runAllProgress: { done: i + 1, total: toRun.length } });
        }
        // Stagger requests: 1s between scraper runs (they take longer)
        if (i < toRun.length - 1) await new Promise((r) => setTimeout(r, 1000));
      }

      qc.invalidateQueries({ queryKey: ['admin-authorities', tabKey] });
    } finally {
      set({
        runningAll: false,
        runAllProgress: { done: 0, total: 0 },
        runAllAbortController: null,
      });
    }
  },

  abortRunAll: () => {
    const controller = get().runAllAbortController;
    if (controller) {
      controller.abort();
    }
  },
}));
